import Lenis from 'lenis';
import './styles.css';

if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

/* Reload reset.
 * The hero cue is `<a href="#philosophy">` — clicking it stamps that
 * hash into the address bar and the browser keeps it across reloads,
 * so refreshing always landed the user mid-page on the Philosophy
 * panel instead of at the hero. We strip the hash on reload so a
 * refresh always starts at the top. Deep links (first visit with a
 * hash from a shared URL) are unaffected — only `type === 'reload'`
 * triggers the wipe. */
{
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  const isReload = nav?.type === 'reload';
  if (isReload && location.hash) {
    history.replaceState(null, '', location.pathname + location.search);
  }
}

if (!location.hash) {
  window.scrollTo(0, 0);
}

/* Canonical URL — rewrite `/index.html` to `/`. Vite serves both, but
 * anchor clicks append the hash to whichever path the user arrived
 * on, producing `/index.html#contact`. Running this before any
 * anchor resolution keeps the address bar clean without a reload. */
if (location.pathname.endsWith('/index.html')) {
  const clean = location.pathname.replace(/\/index\.html$/, '/') + location.search + location.hash;
  history.replaceState(null, '', clean);
}

const body = document.body;
const header = document.querySelector<HTMLElement>('[data-header]');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Footer-nav self-link → scroll to top instead of reload.
 * The footer's Jobs/Imprint/Privacy links point to /jobs etc. With
 * Vercel cleanUrls on, both the address bar and href are extension-
 * less in prod, but Vite dev serves the file at /jobs.html too. So
 * normalize both sides (strip leading slash, strip trailing .html)
 * before comparing — the same code handles dev and prod. */
{
  const normalizePath = (p: string): string =>
    (p.split('?')[0].split('#')[0] || '')
      .toLowerCase()
      .replace(/^\.?\/+/, '')
      .replace(/\.html$/, '')
      .replace(/\/$/, '');
  const currentPath = normalizePath(
    window.location.pathname.split('/').pop() || '',
  );
  document
    .querySelectorAll<HTMLAnchorElement>('.site-footer__nav a')
    .forEach((a) => {
      const raw = a.getAttribute('href') || '';
      if (!raw) return;
      if (normalizePath(raw) !== currentPath) return;
      a.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
}
/* Mirrors the CSS pin-mode gate at .story-pin (styles.css). Below
 * either threshold the story falls back to a carousel-with-buttons
 * (no pinned auto-scroll) so the layout fits and the page doesn't
 * scroll-jack. Keep these in sync with the CSS `@media (min-width:
 * 1024px) and (min-height: 820px)` rule. */
const DESKTOP_STORY_MIN_WIDTH = 1024;
const DESKTOP_STORY_MIN_HEIGHT = 820;

/* ------------------------------------------------------------------
 * RESIZE BUS
 *
 * Window resize used to break the layout in subtle ways: scroll-linked
 * transforms went stale (no scroll event fires on resize alone), the
 * step-mode gate captured `window.innerWidth` at load and never re-
 * evaluated, and individual sections each ran their own un-debounced
 * `resize` listener that fired dozens of times during a drag.
 *
 * Every subsystem now subscribes here:
 *   - `onResizeFrame` runs on every resize, batched into one rAF so
 *     all sections recompute together against the same post-reflow
 *     measurements. Use for stale-transform refresh, header probes,
 *     anything that needs to "redraw" on dimension change.
 *   - `onResizeSettle` runs once after the user stops dragging
 *     (debounced ~180ms). Use for re-anchoring scroll position to a
 *     stable section landmark.
 *
 * Lenis already runs its own ResizeObserver, but we still call
 * `lenis.resize()` explicitly so the per-frame updates below see a
 * fully recalibrated scroller on the very next tick.
 * ------------------------------------------------------------------ */
type ResizeFn = () => void;
const resizeFrameSubs = new Set<ResizeFn>();
const resizeSettleSubs = new Set<ResizeFn>();
const onResizeFrame = (fn: ResizeFn): void => { resizeFrameSubs.add(fn); };
const onResizeSettle = (fn: ResizeFn): void => { resizeSettleSubs.add(fn); };

let resizeFrameQueued = false;
let resizeSettleTimer: number | undefined;
const RESIZE_SETTLE_MS = 180;

/* True from the first resize event of a burst until ~180 ms after the
 * last one. Lets scroll-position trackers freeze their "user intent"
 * snapshot for the duration of the drag — without this freeze, Lenis's
 * resize observer fires scroll events as the document height shifts,
 * and any anchor captured per-tick gets overwritten with intermediate
 * mid-drag positions instead of where the user was before resizing. */
let isResizing = false;
const isResizingNow = (): boolean => isResizing;

/* iPhone Safari (and other mobile browsers) fire `resize` whenever the
 * URL bar shows or hides during scrolling — height changes, width does
 * not. We don't want those to trigger anchor-restore: the user is in
 * the middle of a gesture and snapping back to where they were before
 * the URL bar collapsed reads as the page fighting them. We snapshot
 * the width at the start of each resize burst and only fire the settle
 * subscribers when the width actually changed (real resize / rotation
 * / breakpoint cross), not on chrome transitions. */
let resizeBurstStartWidth = window.innerWidth;

function isDesktopStoryViewport(): boolean {
  return (
    !reduceMotion &&
    window.innerWidth >= DESKTOP_STORY_MIN_WIDTH &&
    window.innerHeight >= DESKTOP_STORY_MIN_HEIGHT
  );
}

let lastDesktopMode = isDesktopStoryViewport();

window.addEventListener('resize', () => {
  if (!isResizing) {
    // First event of a fresh burst — snapshot the width so the settle
    // can tell a real resize apart from an iOS URL-bar transition.
    resizeBurstStartWidth = window.innerWidth;
  }
  isResizing = true;
  if (!resizeFrameQueued) {
    resizeFrameQueued = true;
    requestAnimationFrame(() => {
      resizeFrameQueued = false;
      // Defensive: Lenis observes wrapper/content but the explicit call
      // guarantees its scroll metrics are fresh before subscribers read.
      try { (lenis as unknown as { resize: () => void }).resize(); } catch {}
      for (const fn of resizeFrameSubs) {
        try { fn(); } catch {}
      }
    });
  }
  window.clearTimeout(resizeSettleTimer);
  resizeSettleTimer = window.setTimeout(() => {
    const isDesktop = isDesktopStoryViewport();
    const modeChanged = isDesktop !== lastDesktopMode;
    lastDesktopMode = isDesktop;
    body.classList.toggle('mode-desktop-story', isDesktop);
    // Skip settle subscribers (anchor restore, story re-anchor, mode
    // sync) for height-only resizes: those are iOS Safari URL-bar
    // collapses and on-screen keyboard transitions, not real layout
    // shifts. Re-anchoring on those yanks the page back mid-scroll.
    // Mode change still fires the settle pass — crossing 900px or
    // 620px genuinely needs the layout to re-settle.
    const widthChanged = window.innerWidth !== resizeBurstStartWidth;
    if (widthChanged || modeChanged) {
      for (const fn of resizeSettleSubs) {
        try { fn(); } catch {}
      }
    }
    if (modeChanged) {
      window.dispatchEvent(new CustomEvent('fi:mode-change', { detail: { isDesktop } }));
    }
    // Release the freeze AFTER subscribers run so anchor trackers don't
    // immediately overwrite their pre-resize snapshot with the post-snap
    // position that the settle just produced.
    isResizing = false;
  }, RESIZE_SETTLE_MS);
});

body.classList.toggle('mode-desktop-story', lastDesktopMode);

const restoredFocus = document.activeElement;
if (restoredFocus instanceof HTMLElement && restoredFocus.classList.contains('skip-link')) {
  restoredFocus.blur();
}

/* ------------------------------------------------------------------
 * STEP MODE
 *
 * On desktop with motion allowed, every discrete scroll gesture
 * advances the page by exactly one "stop":
 *   0  Hero top
 *   1  Story — panel 1
 *   2  Story — panel 2
 *   3  Story — panel 3
 *   4  Contact top
 *
 * A wheel burst, arrow key, page key, or touch swipe moves one step
 * regardless of velocity or distance. No multi-section skips, no
 * parking between stops. The existing 1:1 scroll-linked snap/settle
 * systems are disabled in this mode — the step controller owns all
 * forward/backward motion.
 * ------------------------------------------------------------------ */
// Step mode (one-gesture-per-panel snap) only activates when the
// horizontal pin is also active — same gate as canScrollHorizontally
// so the JS doesn't try to snap onto a layout that's been collapsed
// to a vertical stack. Re-evaluated at runtime so resizing across the
// 900px / 620px breakpoints flips the mode without a reload.
const isStepModeActive = (): boolean =>
  isDesktopStoryViewport() && !!document.querySelector('[data-story-pin]');

/* ------------------------------------------------------------------
 * Smooth scroll (Lenis). Disabled under prefers-reduced-motion.
 * ------------------------------------------------------------------ */
const lenis = new Lenis({
  duration: 1.1,
  easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  smoothWheel: !reduceMotion,
  anchors: { offset: 0, onComplete: () => {} },
});

function rafLoop(time: number) {
  lenis.raf(time);
  requestAnimationFrame(rafLoop);
}
requestAnimationFrame(rafLoop);

/* ------------------------------------------------------------------
 * NAVIGATION FLAG
 *
 * Anchor clicks (header "Contact" → #contact, hero cue → #philosophy)
 * trigger a programmatic Lenis scroll. As that scroll passes through
 * the story pin, its entry catch would otherwise fire and hijack the
 * motion partway through — landing the user on Panel 1 instead of
 * the requested target. While this flag is raised, entry catches and
 * the settle stay out of the way.
 * ------------------------------------------------------------------ */
let isNavigating = false;
let navClearTimer: number | undefined;

const beginNavigation = (ms = 1800) => {
  isNavigating = true;
  window.clearTimeout(navClearTimer);
  navClearTimer = window.setTimeout(() => {
    isNavigating = false;
  }, ms);
};

document.addEventListener('click', (e) => {
  const target = e.target as Element | null;
  const anchor = target?.closest<HTMLAnchorElement>('a[href^="#"]');
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || href === '#') return;
  if (!document.querySelector(href)) return;
  beginNavigation();
});

/* ------------------------------------------------------------------
 * STEP CONTROLLER
 *
 * One gesture = one step. Wheel bursts, arrow/page keys, and touch
 * swipes all advance by exactly one stop in the direction of motion,
 * regardless of velocity or distance. Landing animation uses Lenis
 * scrollTo with lock so additional input is absorbed until the step
 * completes — nothing can queue up partial gestures.
 *
 * Stops are derived live from the DOM so resizing works. The whole
 * controller stays loaded; its event handlers no-op when step mode
 * is inactive (narrow viewport, short window, reduced motion), and
 * re-engage automatically when the viewport widens back into range.
 * ------------------------------------------------------------------ */
if (!reduceMotion && document.querySelector('[data-story-pin]')) {
  const STEP_DURATION = 0.9;               // seconds per step
  const MOMENTUM_GAP_MS = 90;              // wheel events must pause this long before the next one counts as a fresh gesture
  const SPIKE_MIN_MAG = 24;                // a "fresh flick during inertia" must have at least this much delta magnitude
  const SPIKE_RATIO = 1.35;                // and must be at least this many × the prior event's magnitude
  const TOUCH_SWIPE_MIN_PX = 32;           // min vertical distance to register a touch as a step
  const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4);

  let locked = false;
  // After a step fires we swallow the trackpad's inertia tail so it
  // doesn't double-trigger. Two signals release the latch:
  //   (1) a real gap in the wheel stream — inertia has stopped, OR
  //   (2) a delta-magnitude spike against the decaying tail — a fresh
  //       deliberate flick layered on top of the existing inertia.
  // (1) alone is too strict: trackpad inertia keeps firing at 16-30 ms
  // intervals, so a same-direction re-flick during it never produces
  // a gap and would otherwise be ignored. (2) catches that case.
  let absorbingMomentum = false;
  let lastWheelTime = 0;
  let lastWheelMag = 0;

  const stepStops = (): number[] => {
    const stops: number[] = [0];
    const storyPin = document.querySelector<HTMLElement>('[data-story-pin]');
    const contactPin = document.querySelector<HTMLElement>('[data-contact-pin]');

    if (storyPin) {
      const storyTop = window.scrollY + storyPin.getBoundingClientRect().top;
      const runway = storyPin.offsetHeight - window.innerHeight;
      if (runway > 0) {
        stops.push(storyTop);                   // panel 1
        stops.push(storyTop + runway * 0.5);    // panel 2
        stops.push(storyTop + runway);          // panel 3
      } else {
        stops.push(storyTop);
      }
    }
    if (contactPin) {
      stops.push(window.scrollY + contactPin.getBoundingClientRect().top);
    }
    return stops.sort((a, b) => a - b);
  };

  const closestStopIndex = (stops: number[]): number => {
    const y = window.scrollY;
    let best = 0;
    let bestDist = Infinity;
    stops.forEach((s, i) => {
      const d = Math.abs(s - y);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  };

  // Cubic ease — gentler tail than quart. Used for the panel-1 → hero
  // step specifically: the distance is long (one full hero runway)
  // and a relaxed settle reads better than the snappier inter-panel
  // decisiveness the other steps want.
  const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);
  const HERO_RETURN_DURATION = 1.35;

  const goStep = (dir: 1 | -1) => {
    if (locked) return;
    const stops = stepStops();
    const cur = closestStopIndex(stops);
    const next = Math.max(0, Math.min(stops.length - 1, cur + dir));
    if (next === cur) return;
    locked = true;
    // Latch momentum absorption for this gesture. The wheel handler's
    // gap+spike detector is the only thing that releases this latch.
    absorbingMomentum = true;
    const returningToHero = next === 0;
    const duration = returningToHero ? HERO_RETURN_DURATION : STEP_DURATION;
    const easing = returningToHero ? easeOutCubic : easeOutQuart;
    // Raise the navigation flag so entry catches skip firing while the
    // step animation passes through their trigger thresholds. Keep the
    // grace short so the user can flick again almost immediately after
    // the animation ends — momentum is now handled by the gap detector
    // below, not by this timer.
    beginNavigation(Math.round(duration * 1000 + 80));
    lenis.scrollTo(stops[next], {
      duration,
      easing,
      lock: true,
      onComplete: () => {
        locked = false;
      },
    });
  };

  // Respect inner scrollable regions (e.g. open chat log marked with
  // data-lenis-prevent) — don't hijack wheel events inside them. Also
  // bail when the user is editing a form field: the step controller
  // would otherwise swallow the spacebar (mapped to "next step") and
  // the user can't type spaces in the chat input.
  const isInsideOptOut = (target: EventTarget | null): boolean => {
    const el = target as Element | null;
    if (!el) return false;
    if (
      el.closest?.(
        'input, textarea, select, [contenteditable=""], [contenteditable="true"]',
      )
    ) {
      return true;
    }
    return !!el.closest?.('[data-lenis-prevent]');
  };

  /* Hero→story is the one transition we leave in the old scroll-linked
   * mode: user scrolls freely through the hero pin while the circle
   * draws, and the hero block snaps forward to story panel 1 on its
   * own when the circle hits the trigger threshold. Step mode only
   * takes over from story onwards. */
  const isAboveStory = (): boolean => {
    const storyPin = document.querySelector<HTMLElement>('[data-story-pin]');
    if (!storyPin) return false;
    return storyPin.getBoundingClientRect().top > 4;
  };

  window.addEventListener(
    'wheel',
    (e: WheelEvent) => {
      // Bail entirely when the layout doesn't pin — narrow viewports
      // and short windows fall back to a vertical stack where free
      // scroll is the right behavior. Re-evaluating per-event keeps
      // resize transitions clean without re-attaching listeners.
      if (!isStepModeActive()) return;
      if (isInsideOptOut(e.target)) return;

      // Track every wheel event's timing/magnitude regardless of which
      // gate fires below — that way the gap and spike comparisons stay
      // accurate when we resume listening after isNavigating clears.
      const now = performance.now();
      const mag = Math.abs(e.deltaY);
      const gap = now - lastWheelTime;
      const prevMag = lastWheelMag;
      lastWheelTime = now;
      lastWheelMag = mag;

      // Programmatic nav in progress (hero circle snap, anchor click,
      // step animation still within its guard window) — absorb wheel
      // so Lenis doesn't accumulate delta that would fight the snap.
      if (isNavigating) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if (isAboveStory()) return;                        // free scroll through hero
      // Block both browser default AND Lenis's own wheel listener.
      // Without stopImmediatePropagation, Lenis still consumes the
      // delta and the user can park between panels.
      e.preventDefault();
      e.stopImmediatePropagation();

      if (absorbingMomentum) {
        const cleanGap = gap >= MOMENTUM_GAP_MS;
        const spike = mag >= SPIKE_MIN_MAG && mag >= prevMag * SPIKE_RATIO;
        if (cleanGap || spike) absorbingMomentum = false;
      }

      if (locked) return;
      if (absorbingMomentum) return;
      goStep(e.deltaY > 0 ? 1 : -1);
    },
    { passive: false, capture: true },
  );

  window.addEventListener('keydown', (e) => {
    if (!isStepModeActive()) return;
    if (isInsideOptOut(e.target)) return;
    if (isNavigating) return;
    if (isAboveStory()) return;      // free scroll through hero
    switch (e.key) {
      case 'ArrowDown':
      case 'PageDown':
      case ' ':
        e.preventDefault();
        goStep(1);
        break;
      case 'ArrowUp':
      case 'PageUp':
        e.preventDefault();
        goStep(-1);
        break;
      case 'Home': {
        e.preventDefault();
        if (locked) break;
        locked = true;
        beginNavigation(Math.max(1200, Math.round(STEP_DURATION * 1000 + 300)));
        lenis.scrollTo(0, {
          duration: STEP_DURATION,
          easing: easeOutQuart,
          lock: true,
          onComplete: () => { locked = false; },
        });
        break;
      }
      case 'End': {
        e.preventDefault();
        if (locked) break;
        const stops = stepStops();
        locked = true;
        beginNavigation(Math.max(1200, Math.round(STEP_DURATION * 1000 + 300)));
        lenis.scrollTo(stops[stops.length - 1], {
          duration: STEP_DURATION,
          easing: easeOutQuart,
          lock: true,
          onComplete: () => { locked = false; },
        });
        break;
      }
    }
  });

  let touchStartY = 0;
  window.addEventListener(
    'touchstart',
    (e) => {
      touchStartY = e.touches[0].clientY;
    },
    { passive: true },
  );
  window.addEventListener(
    'touchend',
    (e) => {
      if (!isStepModeActive()) return;
      if (isInsideOptOut(e.target)) return;
      if (isNavigating) return;
      if (isAboveStory()) return;     // free scroll through hero
      if (locked) return;
      const dy = touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(dy) < TOUCH_SWIPE_MIN_PX) return;
      goStep(dy > 0 ? 1 : -1);
    },
    { passive: true },
  );
}

/* ------------------------------------------------------------------
 * HEADER THEME — swap dark/light variants based on the section
 * currently under the header.
 * ------------------------------------------------------------------ */
/* Track the current theme-color so we don't write the meta tag on
 * every scroll tick — only when the section's theme actually flips. */
let currentThemeColor = '';
const themeColorMeta = document.querySelector<HTMLMetaElement>(
  'meta[name="theme-color"]',
);
const updateThemeColor = (light: boolean): void => {
  if (!themeColorMeta) return;
  // iOS 26 Safari paints the URL-bar chrome (the area surrounding the
  // floating pill) using this meta value. Without per-section updates,
  // crossing into the white story leaves the chrome dark, which reads
  // as a black strip below the section. Match the chrome to the
  // section the user is currently viewing so the bar disappears into
  // the page color.
  const next = light ? '#ffffff' : '#0a0a0a';
  if (next === currentThemeColor) return;
  currentThemeColor = next;
  themeColorMeta.setAttribute('content', next);
};

function setHeaderState() {
  const scrolled = (window.scrollY || 0) > 40;
  body.classList.toggle('is-scrolled', scrolled);

  // Probe 2px below the header's bottom edge. The theme only flips
  // once the new-color section is actually touching the header — no
  // pre-emptive switch while a sliver of the previous section still
  // sits under the header.
  const probeY = Math.min(
    window.innerHeight - 1,
    Math.round((header?.offsetHeight ?? 76) + 2),
  );
  const probe = document.elementFromPoint(
    Math.round(window.innerWidth / 2),
    probeY,
  );
  const themedSection = probe?.closest<HTMLElement>('[data-theme]');
  const isLight = themedSection?.dataset.theme === 'light';
  body.classList.toggle('header-light', isLight);
  body.classList.toggle('header-over-hero', !!probe?.closest('.hero'));
  updateThemeColor(isLight);
}

lenis.on('scroll', setHeaderState);
onResizeFrame(setHeaderState);
setHeaderState();

/* ------------------------------------------------------------------
 * SCROLL ANCHOR — keep the user's place across a resize.
 *
 * When the viewport changes, the document's total scroll height shifts
 * (svh-based pin runways re-measure, mobile fallbacks collapse 240svh
 * stacks into auto-height stacks, etc.). Without intervention, scrollY
 * stays put while the layout slides under it — the user ends up looking
 * at a totally different section than they were a second ago.
 *
 * We track the closest section's top edge on every scroll tick. On
 * resize settle, we restore the scroll offset so that same section
 * still sits at the same fraction of the viewport. Story handles its
 * own panel-precise anchor in its block; this covers hero / contact /
 * the gaps between sections.
 * ------------------------------------------------------------------ */
type Anchor = { el: HTMLElement; offsetFromTop: number } | null;
let anchor: Anchor = null;

const anchorables = (): HTMLElement[] => {
  const sels = ['[data-hero-pin]', '[data-story-pin]', '[data-contact-pin]', 'footer'];
  return sels
    .map((s) => document.querySelector<HTMLElement>(s))
    .filter((el): el is HTMLElement => !!el);
};

const captureAnchor = (): void => {
  // Frozen during a resize burst — see `isResizing` notes. Without
  // this, Lenis's own resize observer emits scroll events as the
  // document height shifts under the user, and the captured anchor
  // drifts to mid-drag values instead of holding the pre-resize
  // section the user was actually reading.
  if (isResizingNow()) return;
  const sections = anchorables();
  const vh = window.innerHeight;
  let best: Anchor = null;
  let bestScore = Infinity;
  for (const el of sections) {
    const rect = el.getBoundingClientRect();
    // Score: distance from section top to viewport top, but only for
    // sections whose top is at or above the viewport top (we're inside
    // them or past them). Add a penalty for sections fully below the
    // fold so the closest "above-or-here" wins.
    const score = rect.top <= 0 ? -rect.top : vh + rect.top;
    if (score < bestScore) {
      bestScore = score;
      best = { el, offsetFromTop: rect.top };
    }
  }
  anchor = best;
};

lenis.on('scroll', captureAnchor);
captureAnchor();

onResizeSettle(() => {
  if (!anchor) return;
  // Story owns its own panel-precise re-anchor (its lastActiveDot
  // tracks which of three panels was visible). When the anchor is the
  // story-pin and we're inside its runway, defer to that handler.
  const isStory = anchor.el.dataset.storyPin !== undefined
    || anchor.el.matches('[data-story-pin]');
  if (isStory) {
    const rect = anchor.el.getBoundingClientRect();
    const runway = anchor.el.offsetHeight - window.innerHeight;
    const inRunway = rect.top <= 0 && -rect.top <= runway && runway > 0;
    if (inRunway) return;
  }
  const rect = anchor.el.getBoundingClientRect();
  const targetY = window.scrollY + rect.top - anchor.offsetFromTop;
  if (Math.abs(targetY - window.scrollY) > 1) {
    lenis.scrollTo(targetY, { immediate: true });
  }
  captureAnchor();
});

/* ------------------------------------------------------------------
 * HERO — rotating italic word.
 * All words stack in a single inline-grid cell sized by the widest.
 * Shorter words get letter-spacing padding so every word fills the
 * slot end-to-end. The surrounding text never reflows.
 * ------------------------------------------------------------------ */
const rotator = document.querySelector<HTMLElement>('[data-rotator]');

if (rotator) {
  const wordEls = Array.from(
    rotator.querySelectorAll<HTMLElement>('.rotator__word'),
  );

  const tuneWidths = () => {
    for (const el of wordEls) el.style.letterSpacing = '';

    const widths = wordEls.map((el) => el.getBoundingClientRect().width);
    const max = Math.max(...widths);

    wordEls.forEach((el, i) => {
      const text = el.textContent ?? '';
      const charCount = text.length;
      if (charCount < 2) return;
      const delta = max - widths[i];
      if (delta < 0.5) return;
      el.style.letterSpacing = `${delta / charCount}px`;
    });
  };

  tuneWidths();

  if ('fonts' in document) {
    document.fonts.ready.then(tuneWidths).catch(() => {});
  }

  // Width tuning runs on the settle pass — the per-frame redraw isn't
  // needed because the rotator's surrounding text doesn't reflow until
  // the next word swap, and re-measuring on every resize tick during a
  // drag was the source of visible letter-spacing jitter.
  onResizeSettle(tuneWidths);

  if (wordEls.length > 1 && !reduceMotion) {
    const ENTER_DELAY_MS = 120;
    const RESET_DELAY_MS = 460;
    const INTERVAL_MS = 2800;

    let activeIdx = 0;

    const cycle = () => {
      const current = wordEls[activeIdx];
      activeIdx = (activeIdx + 1) % wordEls.length;
      const next = wordEls[activeIdx];

      current.classList.remove('is-active');
      current.classList.add('is-leaving');

      window.setTimeout(() => {
        next.classList.add('is-active');
      }, ENTER_DELAY_MS);

      window.setTimeout(() => {
        current.classList.remove('is-leaving');
      }, RESET_DELAY_MS);
    };

    window.setInterval(cycle, INTERVAL_MS);
  }
}

/* ------------------------------------------------------------------
 * SCROLL SYSTEM
 *
 * Three pinned stages, each tuned to feel directly linked to user
 * scroll input. Nothing snaps mid-scroll — all "snap" behavior is an
 * entry catch that fires once when the stage is first reached.
 *
 *   1) HERO     — scroll-drawn circle. Circle fills across the pin;
 *                 the last 40% of the draw overlaps the hero scrolling
 *                 away, so the handoff to the next section is dynamic.
 *   2) STORY    — horizontal track translates 1:1 with scroll. Panel 1
 *                 locks on entry; inside the pin nothing snaps, you
 *                 just glide through at your own pace (Lenis smooths
 *                 the input).
 *   3) CONTACT  — pinned full-viewport section with an entry catch
 *                 (snaps fully into view) and an underline that fills
 *                 on the italic word as you scroll through.
 *
 * Shared helpers below, then each stage in its own block.
 * ------------------------------------------------------------------ */

/* Decisive deceleration — used by every entry catch for consistency. */
const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4);

/* Entry catch: once, when the user first crosses into a pinned
 * section from above, call lenis.scrollTo to glide to the pin's top
 * edge. The section then sits fully in view before the user continues.
 *
 * - Duration scales inversely with the user's scroll velocity so a
 *   fast flick gets a short catch and a gentle arrival gets a longer
 *   one. Both feel connected because `lock: true` absorbs input for
 *   the catch span only — afterwards user is free.
 * - If the user is scrolling UP past the top of the pin, we don't
 *   catch (that would feel like the page is fighting them).
 * - Fires once per top-down entry; re-arms when fully above again. */
const VELOCITY_REFERENCE = 2500; // px/s, a fast flick
const CATCH_MIN_DURATION = 0.55;
const CATCH_MAX_DURATION = 1.05;

const projectCatchDuration = (peakVelocity: number): number => {
  const t = Math.min(1, peakVelocity / VELOCITY_REFERENCE);
  const eased = 1 - Math.pow(1 - t, 2);
  return CATCH_MAX_DURATION - (CATCH_MAX_DURATION - CATCH_MIN_DURATION) * eased;
};

const attachEntryCatch = (
  pinEl: HTMLElement,
  opts: { triggerAt?: () => number } = {},
): void => {
  if (reduceMotion) return;

  /* Distance above viewport top at which the catch arms. Default is
   * 4px — the pin has to be basically docked. Contact overrides this
   * so the snap fires while the pin is still entering from below,
   * removing the dead zone between story's horizontal end and contact
   * arriving. */
  const triggerAt = opts.triggerAt ?? (() => 4);

  // Seed `wasAbove` from the initial position so a page-load, hash jump,
  // or refresh inside the pin doesn't trigger a fight-the-user catch.
  let wasAbove = pinEl.getBoundingClientRect().top > triggerAt();
  let scrollDir = 0;
  let prevY = window.scrollY;
  let peakV = 0;

  lenis.on('scroll', () => {
    const dy = window.scrollY - prevY;
    if (Math.abs(dy) > 0.5) scrollDir = dy > 0 ? 1 : -1;
    prevY = window.scrollY;

    const v = Math.abs((lenis as unknown as { velocity?: number }).velocity ?? 0);
    if (v > peakV) peakV = v;

    const rect = pinEl.getBoundingClientRect();
    const nowInPin = rect.top <= 0 && rect.bottom >= window.innerHeight;
    const threshold = triggerAt();

    // Bookkeeping still runs during anchor navigation — we just don't
    // fire the catch itself. That way re-arming still works correctly
    // and the catch is ready next time the user scrolls in manually.
    if (isNavigating) {
      if (!nowInPin && rect.top > threshold) wasAbove = true;
      else if (nowInPin) wasAbove = false;
      return;
    }

    // Mobile / narrow / short viewport: the section is in normal flow,
    // not pinned. Catching with lock:true would freeze the user's touch
    // mid-scroll. Bookkeep `wasAbove` so a resize back into desktop
    // mode picks up cleanly, but skip the snap.
    if (!isDesktopStoryViewport()) {
      if (!nowInPin && rect.top > threshold) wasAbove = true;
      else if (nowInPin) wasAbove = false;
      return;
    }

    if (wasAbove && scrollDir > 0 && rect.top <= threshold && rect.top >= -window.innerHeight * 0.25) {
      // Entered the pin's trigger window from above with forward
      // motion — catch it.
      wasAbove = false;
      const pinTopY = window.scrollY + rect.top;
      lenis.scrollTo(pinTopY, {
        duration: projectCatchDuration(peakV),
        easing: easeOutQuart,
        lock: true,
      });
      peakV = 0;
    } else if (!nowInPin && rect.top > threshold) {
      // Fully above the pin's trigger window again — re-arm
      wasAbove = true;
    }
  });
};

/* ------------------------------------------------------------------
 * HERO — scroll-drawn circle around "make it real."
 *
 * Pin runway is sized so the full scroll budget is ~0.75vh. The
 * circle fills across progress 0→CIRCLE_COMPLETE_AT. Because
 * CIRCLE_COMPLETE_AT > 1, the last portion of the draw plays WHILE
 * the hero is already scrolling up — the user's reward for continuing.
 * ------------------------------------------------------------------ */
{
  const heroPin = document.querySelector<HTMLElement>('[data-hero-pin]');
  const heroEl = heroPin?.querySelector<HTMLElement>('.hero');
  const circlePath = document.querySelector<SVGPathElement>(
    '[data-scroll-circle] path',
  );

  /* Progress (0 = pin start, 1 = pin release) at which the circle is
   * fully drawn. > 1 means it completes after the hero has begun
   * scrolling away, so the draw overlaps the handoff. */
  const CIRCLE_COMPLETE_AT = 1.35;

  /* Cue appears when circle is this far drawn. Low so the "you can
   * continue" signal arrives before the final stretch. */
  const CUE_VISIBLE_AT = 0.55;

  /* Once the circle reaches this fill fraction while the user is
   * scrolling forward, the page snaps ahead to story panel 1. The
   * threshold sits high enough that the "closure" of the circle
   * reads as a triggered gesture, not an interruption. The flag
   * re-arms when the circle retreats well below, so scrolling back
   * up and forward again still works. */
  const SNAP_ADVANCE_AT = 0.9;
  const SNAP_REARM_BELOW = 0.5;

  if (heroPin && circlePath && !reduceMotion) {
    let ticking = false;
    let isComplete = false;
    let snapArmed = true;
    let prevScrollY = window.scrollY;
    let lastFill = 0;

    const update = () => {
      ticking = false;
      const rect = heroPin.getBoundingClientRect();
      const runway = heroPin.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      // No upper cap — progress > 1 means pin has released; circle keeps drawing.
      const progress = runway > 0 ? scrolled / runway : 0;

      // Direction detection. Both checks read against the previous
      // tick's scrollY, so we compute both BEFORE updating prevScrollY.
      // (Previously we updated prevScrollY between the two checks,
      // which silently zeroed out `scrollingForward` and meant the
      // auto-snap to story never fired — the user would be parked on
      // hero with a fully drawn circle.)
      const scrollingBack = window.scrollY < prevScrollY - 0.5;
      const scrollingForward = window.scrollY > prevScrollY + 0.5;
      prevScrollY = window.scrollY;

      const forwardFill = Math.max(0, Math.min(1, progress / CIRCLE_COMPLETE_AT));
      const backwardFill = Math.max(0, Math.min(1, progress));
      if (scrollingBack) lastFill = Math.min(lastFill, backwardFill);
      else lastFill = forwardFill;
      const circleProgress = lastFill;

      circlePath.style.strokeDashoffset = (1 - circleProgress).toString();

      const complete = circleProgress >= CUE_VISIBLE_AT;
      if (complete !== isComplete && heroEl) {
        isComplete = complete;
        heroEl.classList.toggle('is-circle-complete', complete);
      }

      if (isStepModeActive() && !isNavigating) {
        if (snapArmed && scrollingForward && circleProgress >= SNAP_ADVANCE_AT) {
          snapArmed = false;
          const storyPin = document.querySelector<HTMLElement>('[data-story-pin]');
          if (storyPin) {
            const storyTopY = window.scrollY + storyPin.getBoundingClientRect().top;
            // Gate entry catches — otherwise the story entry catch
            // would see the forward motion and fire its own scrollTo
            // on top of ours.
            beginNavigation(1200);
            lenis.scrollTo(storyTopY, {
              duration: 0.9,
              easing: (t: number) => 1 - Math.pow(1 - t, 4),
              lock: true,
            });
          }
        } else if (circleProgress < SNAP_REARM_BELOW) {
          snapArmed = true;
        }
      }
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    lenis.on('scroll', onScroll);
    onResizeFrame(update);
    update();
  }
}

/* ------------------------------------------------------------------
 * STORY — pinned horizontal scroll, 1:1 with page scroll.
 *
 * No panel-to-panel snapping. The track translates linearly with
 * progress through the pin; Lenis already smooths the input, so the
 * motion feels both glued to the user's gesture and gently eased.
 *
 * The only snap is an entry catch that locks Panel 1 into view when
 * you first cross into the pin from above. After that, you're free.
 * ------------------------------------------------------------------ */
{
  const storyPin = document.querySelector<HTMLElement>('[data-story-pin]');
  const storyEl = storyPin?.querySelector<HTMLElement>('.story') ?? null;
  const storyTrack = document.querySelector<HTMLElement>('[data-story-track]');
  const storyDots = Array.from(
    document.querySelectorAll<HTMLElement>('[data-story-progress] .story__dot'),
  );

  const PANEL_COUNT = 3;

  // Mirror the CSS gate that activates the horizontal pin: motion
  // allowed AND wide enough AND tall enough. Below any of these,
  // the story falls back to a vertical stack and the step/snap
  // logic should stay out of the way.
  const canScrollHorizontally = () =>
    !reduceMotion &&
    window.innerWidth >= DESKTOP_STORY_MIN_WIDTH &&
    window.innerHeight >= DESKTOP_STORY_MIN_HEIGHT;

  /* Per-gesture panel commit. Each discrete user gesture (wheel burst,
   * trackpad swipe, touch drag) snaps to exactly one neighbour — or
   * bounces back to the panel it started from if the user didn't
   * push far enough. Feels connected because the 1:1 translation still
   * tracks the live gesture; feels committed because the gesture ends
   * on a clean slide boundary every time.
   *
   *  - COMMIT_THRESHOLD: fraction of a panel-step the user has to
   *    travel during the gesture to count as "going next". Below that,
   *    we snap back to the start panel.
   *  - VELOCITY_COMMIT_BOOST: on fast flicks we add this many panels'
   *    worth of intent before rounding, so a snappy flick reliably
   *    advances (and can skip a panel) without requiring you to drag
   *    all the way across. */
  const PANEL_STEP = 1 / (PANEL_COUNT - 1);
  const COMMIT_THRESHOLD = 0.18;        // 18% of a panel-step commits to next
  const VELOCITY_COMMIT_BOOST = 0.6;    // extra panels added on fast flicks
  const SETTLE_IDLE_MS = 60;            // wait for Lenis momentum to quiet
  const SETTLE_PIN_EDGE_BUFFER = 24;    // don't snap near the pin's entry/exit edges

  /* Velocity-inheriting decay for the commit. Lenis's lerp mode starts
   * from the current scroll velocity and decays continuously toward
   * the target, so the snap reads as the flick *easing out* rather
   * than a new animation restarting from zero. Range is direction-
   * agnostic — we just want one smooth deceleration curve.
   *   - LERP_MIN: gentle nudges → slower, softer drift
   *   - LERP_MAX: fast flicks → snappier decay so they don't overshoot */
  const COMMIT_LERP_MIN = 0.08;
  const COMMIT_LERP_MAX = 0.13;

  if (storyPin && storyTrack) {
    let ticking = false;
    let lastActiveDot = -1;
    let settleTimer: number | undefined;
    let peakV = 0;
    let scrollDir = 0;
    let prevY = window.scrollY;

    // Gesture bookkeeping. `gestureActive` flips true on the first user
    // input tick after a settle; `gestureStartRaw` freezes the progress
    // at that moment so we can measure travel when the gesture ends.
    let gestureActive = false;
    let gestureStartRaw = 0;

    const currentRaw = () => {
      const rect = storyPin.getBoundingClientRect();
      const runway = storyPin.offsetHeight - window.innerHeight;
      if (runway <= 0) return 0;
      const scrolled = Math.max(0, -rect.top);
      return Math.max(0, Math.min(1, scrolled / runway));
    };

    const setActiveDot = (idx: number) => {
      if (idx === lastActiveDot) return;
      lastActiveDot = idx;
      storyDots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === idx);
      });
    };

    const update = () => {
      ticking = false;

      if (!canScrollHorizontally()) {
        // Carousel mode owns the transform now — bail without
        // touching it so the user's selected panel doesn't get
        // wiped on every scroll tick.
        return;
      }

      const rect = storyPin.getBoundingClientRect();
      const runway = storyPin.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const raw =
        runway > 0 ? Math.max(0, Math.min(1, scrolled / runway)) : 0;

      const trackWidth = storyTrack.scrollWidth;
      const viewport = window.innerWidth;
      const maxTranslate = Math.max(0, trackWidth - viewport);

      storyTrack.style.transform = `translate3d(${-raw * maxTranslate}px, 0, 0)`;

      const activeIdx = Math.round(raw * (PANEL_COUNT - 1));
      setActiveDot(activeIdx);
    };

    const onScroll = () => {
      const dy = window.scrollY - prevY;
      if (Math.abs(dy) > 0.5) scrollDir = dy > 0 ? 1 : -1;
      prevY = window.scrollY;

      const v = Math.abs((lenis as unknown as { velocity?: number }).velocity ?? 0);
      if (v > peakV) peakV = v;

      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    const commitPanel = () => {
      gestureActive = false;
      if (!canScrollHorizontally()) return;
      if (isNavigating) return;
      const rect = storyPin.getBoundingClientRect();
      const runway = storyPin.offsetHeight - window.innerHeight;
      if (runway <= 0) return;

      const scrolled = -rect.top;
      // Outside the pin there's nothing to commit onto.
      if (scrolled < SETTLE_PIN_EDGE_BUFFER) return;
      if (scrolled > runway - SETTLE_PIN_EDGE_BUFFER) return;

      const raw = Math.max(0, Math.min(1, scrolled / runway));
      const startPanel = Math.round(gestureStartRaw * (PANEL_COUNT - 1));

      // Travel since gesture started, in panel units. Velocity adds a
      // directional nudge so a fast flick commits (and can skip panels)
      // without requiring a full-panel drag.
      const travel = (raw - gestureStartRaw) / PANEL_STEP;
      const vNorm = Math.min(1, peakV / VELOCITY_REFERENCE);
      const vBoost = scrollDir === 0 ? 0 : scrollDir * vNorm * VELOCITY_COMMIT_BOOST;
      const effective = travel + vBoost;

      let targetPanel = startPanel;
      if (effective > COMMIT_THRESHOLD) {
        targetPanel = startPanel + Math.max(1, Math.round(effective));
      } else if (effective < -COMMIT_THRESHOLD) {
        targetPanel = startPanel + Math.min(-1, Math.round(effective));
      }
      targetPanel = Math.max(0, Math.min(PANEL_COUNT - 1, targetPanel));

      // Absolute-position override: if we ended up very close to a
      // panel (≥80% of the way there) that the gesture logic didn't
      // pick, prefer that panel. Catches the case where cumulative
      // scroll leaves you parked just shy of a boundary even though
      // the final gesture itself was too small to commit.
      const nearestPanel = Math.round(raw * (PANEL_COUNT - 1));
      const distToNearest = Math.abs(raw - nearestPanel * PANEL_STEP) / PANEL_STEP;
      if (distToNearest < 0.2) targetPanel = nearestPanel;

      const target = targetPanel * PANEL_STEP;
      if (Math.abs(target - raw) < 0.002) {
        peakV = 0;
        return;
      }

      // Lerp decays continuously from the current scroll velocity, so
      // the snap reads as the flick easing out rather than a new
      // animation starting from zero. Faster flicks get a snappier
      // decay so they don't overshoot-feel on arrival.
      const lerp = COMMIT_LERP_MIN + (COMMIT_LERP_MAX - COMMIT_LERP_MIN) * vNorm;

      const pinTopY = window.scrollY + rect.top;
      const targetY = pinTopY + target * runway;

      // No `lock` — user can override mid-animation by scrolling again.
      lenis.scrollTo(targetY, { lerp });

      peakV = 0;
    };

    // Called on every user input tick. First tick after a commit starts
    // a fresh gesture (we capture the progress it began from); subsequent
    // ticks just refresh the idle timer.
    const onUserInput = () => {
      if (!canScrollHorizontally()) return;
      // Step mode drives panel motion via explicit stops — the
      // gesture-based commit here would fight it.
      if (isStepModeActive()) return;
      if (!gestureActive) {
        gestureActive = true;
        gestureStartRaw = currentRaw();
        peakV = 0;
      }
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(commitPanel, SETTLE_IDLE_MS);
    };
    const scheduleSettle = onUserInput;

    lenis.on('scroll', onScroll);

    // Resize re-anchor. When the viewport height shrinks, .story-pin's
    // pixel height (240svh) shrinks proportionally — same scrollY now
    // sits deeper inside the shorter runway, so the track auto-
    // translates forward and the panels appear to "scroll on their
    // own." Snap scroll to the current panel's stop in the new
    // runway so the visible panel stays put across resizes.
    // Frame pass: redraw the track transform against the new viewport
    // so the desktop pixel-transform doesn't drift mid-drag. Cheap;
    // safe to run every resize tick. Carousel mode (mobile) sets its
    // own transform via syncMode on settle — no work needed here.
    onResizeFrame(() => { update(); });

    // Settle pass: once the drag stops, re-anchor scroll so the panel
    // that was visible before the resize is still the panel docked
    // after. Without this, scrollY stays put while the runway shrinks,
    // and the track silently advances or retreats by a panel.
    onResizeSettle(() => {
      if (!canScrollHorizontally()) return;
      const rect = storyPin.getBoundingClientRect();
      const runway = storyPin.offsetHeight - window.innerHeight;
      if (runway <= 0) return;
      const scrolled = -rect.top;
      if (scrolled < 0 || scrolled > runway) return;
      const idx = lastActiveDot;
      if (idx < 0) return;
      const targetY = window.scrollY + rect.top + (idx * PANEL_STEP) * runway;
      if (Math.abs(targetY - window.scrollY) > 1) {
        lenis.scrollTo(targetY, { immediate: true });
      }
    });

    // Settle fires only after user input goes quiet, not from programmatic
    // scroll. Entry catch and settle itself both trigger via lenis.scrollTo,
    // which doesn't re-trigger these listeners.
    window.addEventListener('wheel', scheduleSettle, { passive: true });
    window.addEventListener('touchmove', scheduleSettle, { passive: true });
    window.addEventListener('touchend', scheduleSettle, { passive: true });
    window.addEventListener('keydown', (e) => {
      const scrollKeys = [
        'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp',
        ' ', 'Home', 'End',
      ];
      if (scrollKeys.includes(e.key)) scheduleSettle();
    });

    // Always attach — the catch's own gate fires only when the section
    // is actually pinned (rect.top crosses zero from above), which
    // never happens in the vertical-stack fallback. Attaching
    // unconditionally means resizing into desktop mode after page load
    // doesn't leave the section without an entry catch.
    attachEntryCatch(storyPin);

    /* ----------------------------------------------------------------
     * MOBILE / FALLBACK CAROUSEL
     *
     * When the horizontal pin is off (narrow viewport, short window,
     * reduced motion), the same .story__track becomes a click-driven
     * carousel: prev / next buttons + dots advance the panel via a
     * CSS transition, and the page scrolls past the section normally.
     * No scroll capture, no pin, no Lenis re-anchor — the user can
     * keep going whenever they want.
     *
     * Desktop's scroll-driven update() owns `transform` while
     * canScrollHorizontally() is true; the carousel owns it in the
     * fallback. Both write to the same inline style, so we clear
     * the carousel's transform when crossing back into desktop mode
     * and clear desktop's transform when crossing into mobile.
     * ---------------------------------------------------------------- */
    const navPrev = document.querySelector<HTMLButtonElement>('[data-story-nav-prev]');
    const navNext = document.querySelector<HTMLButtonElement>('[data-story-nav-next]');
    const navDots = Array.from(
      document.querySelectorAll<HTMLButtonElement>('[data-story-nav-dot]'),
    );

    let carouselIdx = 0;
    const applyCarousel = (idx: number) => {
      carouselIdx = Math.max(0, Math.min(PANEL_COUNT - 1, idx));
      storyTrack.style.transform = `translate3d(${-carouselIdx * 100}%, 0, 0)`;
      navDots.forEach((dot, i) => dot.classList.toggle('is-active', i === carouselIdx));
      if (navPrev) navPrev.disabled = carouselIdx === 0;
      if (navNext) navNext.disabled = carouselIdx === PANEL_COUNT - 1;
    };

    const isCarousel = () => !canScrollHorizontally();

    navPrev?.addEventListener('click', () => {
      if (!isCarousel()) return;
      applyCarousel(carouselIdx - 1);
    });
    navNext?.addEventListener('click', () => {
      if (!isCarousel()) return;
      applyCarousel(carouselIdx + 1);
    });
    navDots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        if (!isCarousel()) return;
        applyCarousel(i);
      });
    });

    /* ── Horizontal swipe gesture (mobile carousel) ───────────────
     * touch-action: pan-y on .story__panel lets the browser handle
     * vertical pans natively (page scroll) while leaving horizontal
     * gestures available for JS. We just measure the touch delta on
     * release: if it's horizontal-dominant and big enough, advance
     * (or retreat) the carousel by one panel.
     *
     * Thresholds: 48px min distance (filters taps + tiny drags), and
     * |dx| > |dy| (filters incidental horizontal drift during a
     * vertical scroll). No max time — a slow but committed swipe
     * still counts. */
    const SWIPE_MIN_PX = 48;
    let touchStartX = 0;
    let touchStartY = 0;
    let swipeActive = false;

    storyEl?.addEventListener('touchstart', (e) => {
      if (!isCarousel()) return;
      // Don't capture taps that originated on the nav buttons —
      // their click handlers already drive the carousel. Listening
      // here would double-fire on the next touchend.
      const target = e.target as Element | null;
      if (target?.closest('.story__nav-btn, .story__nav-dot')) return;
      const t = e.touches[0];
      touchStartX = t.clientX;
      touchStartY = t.clientY;
      swipeActive = true;
    }, { passive: true });

    storyEl?.addEventListener('touchend', (e) => {
      if (!swipeActive) return;
      swipeActive = false;
      if (!isCarousel()) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStartX;
      const dy = t.clientY - touchStartY;
      if (Math.abs(dx) < SWIPE_MIN_PX) return;
      // Horizontal-dominant: |dx| must exceed |dy| so a vertical
      // scroll with slight horizontal drift doesn't flip a panel.
      if (Math.abs(dx) <= Math.abs(dy)) return;
      // Swipe left (dx < 0) → next panel; swipe right → previous.
      applyCarousel(carouselIdx + (dx < 0 ? 1 : -1));
    }, { passive: true });

    storyEl?.addEventListener('touchcancel', () => {
      swipeActive = false;
    }, { passive: true });

    /* ── Desktop orb cursor + click-to-advance ──────────────────
     * Wide-but-short desktop windows hide the chevron buttons (CSS
     * media query) and use a custom black orb cursor that follows
     * the mouse. The orb shows a left or right arrow based on which
     * half of the viewport the pointer is on; clicking that half
     * advances the carousel in that direction. The orb dims at the
     * carousel's edges so the user knows they can't go further.
     *
     * Mobile / reduced-motion users keep the chevron tap targets;
     * this whole block is gated by isDesktopCarousel(). */
    const isDesktopCarousel = (): boolean =>
      !reduceMotion &&
      window.innerWidth >= DESKTOP_STORY_MIN_WIDTH &&
      window.innerHeight < DESKTOP_STORY_MIN_HEIGHT;

    let cursorEl: HTMLDivElement | null = null;
    const ensureCursor = (): HTMLDivElement => {
      if (cursorEl) return cursorEl;
      const el = document.createElement('div');
      el.className = 'story-cursor';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML =
        '<svg viewBox="0 0 18 18" aria-hidden="true">'
        + '<path d="M5 9 L13 9 M9 5 L13 9 L9 13"/>'
        + '</svg>';
      document.body.appendChild(el);
      cursorEl = el;
      return el;
    };

    const setCursor = (clientX: number, clientY: number): void => {
      if (!cursorEl) return;
      const dir = clientX < window.innerWidth / 2 ? 'left' : 'right';
      const atEdge =
        (dir === 'left' && carouselIdx === 0)
        || (dir === 'right' && carouselIdx === PANEL_COUNT - 1);
      cursorEl.dataset.dir = dir;
      cursorEl.dataset.disabled = atEdge ? 'true' : 'false';
      // Center the 64px orb on the pointer.
      cursorEl.style.transform =
        `translate3d(${clientX - 32}px, ${clientY - 32}px, 0)`;
    };

    const showCursor = (): void => {
      if (!isDesktopCarousel()) return;
      ensureCursor().classList.add('is-visible');
      storyEl?.classList.add('is-orb-active');
    };
    const hideCursor = (): void => {
      cursorEl?.classList.remove('is-visible');
      storyEl?.classList.remove('is-orb-active');
    };

    /* Pointer tracking at the document level (not just on storyEl).
     * mouseenter/mouseleave on storyEl are unreliable for our use:
     * - The user can scroll past the section without ever moving the
     *   mouse out of its viewport bounding box, leaving the orb
     *   stranded over a different section (Contact).
     * - The chat overlay is fixed-position above .story; opening it
     *   doesn't fire a leave event on .story underneath.
     * Solution: a single document-level mousemove that hit-tests the
     * event target, plus a scroll guard that hides the orb whenever
     * the user scrolls (the cached pointer position is now stale). */
    const pointerOverStory = (target: Element | null): boolean => {
      if (!target) return false;
      // Chat overlay covers the page when open — even if the original
      // hit-test would land on .story, the overlay is what's actually
      // under the cursor. Treat is-chat-open as "not over story".
      if (document.documentElement.classList.contains('is-chat-open')) return false;
      return !!target.closest('.story');
    };

    document.addEventListener('mousemove', (e) => {
      if (!isDesktopCarousel()) {
        if (cursorEl?.classList.contains('is-visible')) hideCursor();
        return;
      }
      if (pointerOverStory(e.target as Element | null)) {
        ensureCursor();
        setCursor(e.clientX, e.clientY);
        if (!cursorEl?.classList.contains('is-visible')) showCursor();
      } else if (cursorEl?.classList.contains('is-visible')) {
        hideCursor();
      }
    });

    /* Scrolling moves content under a still mouse — the orb's cached
     * position is now over a different element. Hide it; the next
     * mousemove will reposition and re-show if appropriate. */
    window.addEventListener('scroll', () => {
      if (cursorEl?.classList.contains('is-visible')) hideCursor();
    }, { passive: true });

    /* Click-to-advance — left half goes back, right half goes
     * forward. Filter out drags (so text selection doesn't fire
     * an advance) and clicks on interactive descendants (dots and
     * any in-content links). */
    let mousedownX = 0;
    let mousedownY = 0;
    storyEl?.addEventListener('mousedown', (e) => {
      mousedownX = e.clientX;
      mousedownY = e.clientY;
    });
    storyEl?.addEventListener('click', (e) => {
      if (!isDesktopCarousel()) return;
      const target = e.target as Element | null;
      if (target?.closest('a, button, input, textarea, [role="button"]')) return;
      const dx = e.clientX - mousedownX;
      const dy = e.clientY - mousedownY;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) return;   // dragged → ignore
      applyCarousel(carouselIdx + (e.clientX < window.innerWidth / 2 ? -1 : 1));
      setCursor(e.clientX, e.clientY);                    // refresh disabled state
    });

    /* Arrow-key navigation. Active in any carousel mode (mobile or
     * desktop short) as long as the story occupies the centre of the
     * viewport and the user isn't typing in a form field. */
    window.addEventListener('keydown', (e) => {
      if (!isCarousel()) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const active = document.activeElement as HTMLElement | null;
      if (
        active
        && (active.tagName === 'INPUT'
          || active.tagName === 'TEXTAREA'
          || active.isContentEditable)
      ) return;
      if (!storyEl) return;
      const rect = storyEl.getBoundingClientRect();
      const mid = window.innerHeight / 2;
      if (rect.top > mid || rect.bottom < mid) return;     // .story not centred
      e.preventDefault();
      applyCarousel(carouselIdx + (e.key === 'ArrowLeft' ? -1 : 1));
    });

    // Mode switch hygiene: desktop's scroll-driven update() and the
    // carousel both target the track's inline transform. When the
    // viewport crosses the boundary we wipe the inline value and let
    // the active mode redraw it cleanly on its next tick.
    const syncMode = () => {
      if (isCarousel()) {
        applyCarousel(carouselIdx);
      } else {
        // Desktop's update() writes its own transform on the next
        // scroll tick — clearing here just removes the carousel's
        // percentage value so the desktop pixel-based transform
        // doesn't briefly fight it on first paint.
        storyTrack.style.transform = '';
      }
      // Pin-mode resumes its own native cursor, so any orb left over
      // from the carousel needs to retire.
      if (!isDesktopCarousel()) hideCursor();
    };
    onResizeSettle(syncMode);
    syncMode();

    update();
  }
}

/* ------------------------------------------------------------------
 * CONTACT — pinned section with entry catch and underline fill.
 *
 * The pin gives us a scroll budget while contact is fully in view.
 * The italic word "building." gets a hand-drawn underline that fills
 * across the pin — the same "filling" gesture as the hero circle, so
 * the page opens and closes on the same visual beat.
 * ------------------------------------------------------------------ */
{
  const contactPin = document.querySelector<HTMLElement>('[data-contact-pin]');
  const underlinePath = document.querySelector<SVGPathElement>(
    '[data-scroll-underline] path',
  );

  /* Drive the underline off the APPROACH window — the range where
   * contact-pin is sliding up into view (pin top goes from a viewport
   * below to the viewport top). It completes exactly when the contact
   * section is fully in view, before the pin even starts pinning. So
   * no extra scroll inside the pin is needed.
   *
   * APPROACH_START_FRACTION < 1 means the draw begins while contact
   * is still mostly below the fold (a subtle reveal as it arrives),
   * and reaches 100% right as it docks into view. */
  const APPROACH_START_FRACTION = 0.85;

  if (contactPin && underlinePath && !reduceMotion) {
    let ticking = false;

    const update = () => {
      ticking = false;
      const rect = contactPin.getBoundingClientRect();
      const vh = window.innerHeight;
      // rect.top:  +vh → contact bottom just touches viewport bottom
      //               0 → contact top docked at viewport top (fully in view)
      //              <0 → inside the pin
      const approach = 1 - Math.max(0, rect.top) / (vh * APPROACH_START_FRACTION);
      const p = Math.max(0, Math.min(1, approach));
      underlinePath.style.strokeDashoffset = (1 - p).toString();
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    lenis.on('scroll', onScroll);
    onResizeFrame(update);
    // Fire the contact catch while contact is still ~70% below the
    // fold. Between the story's horizontal ending (runway done) and
    // contact docking at the viewport top there's a ~1 viewport
    // transition where both sections share the screen. This early
    // trigger turns that drift into a clean snap.
    // Panel 3 → contact is free-scroll in step mode, so the entry
    // catch is needed to snap that transition shut.
    attachEntryCatch(contactPin, {
      triggerAt: () => window.innerHeight * 0.7,
    });
    update();
  }
}

/* ------------------------------------------------------------------
 * SECTION-STRADDLE SETTLE — "never park between sections."
 *
 * The forward-only entry catches handle snapping during active
 * downward gestures. This settler covers the remaining cases:
 *   - user scrolls DOWN gently and stops mid-transition (catch
 *     didn't arm because motion was too slow),
 *   - user scrolls UP from a pin and parks mid-transition (catches
 *     only fire forward).
 *
 * After input goes idle, if the viewport is straddling a pin boundary
 * (pin top sits anywhere between -50% and +70% of the viewport) we
 * decide: commit forward into that pin, or retreat out of it, based
 * on which edge is closer. Either way the user lands on a section.
 * ------------------------------------------------------------------ */
{
  const storyPin = document.querySelector<HTMLElement>('[data-story-pin]');
  const contactPin = document.querySelector<HTMLElement>('[data-contact-pin]');
  const targets = [storyPin, contactPin].filter(
    (el): el is HTMLElement => !!el,
  );

  if (!reduceMotion && targets.length > 0) {
    const STRADDLE_IDLE_MS = 140;
    const STRADDLE_ENTER_MAX = 0.7;   // pin.top ≤ 70% of vh → straddling from below
    const STRADDLE_EXIT_MIN = -0.5;   // pin.top ≥ -50% of vh → straddling from above
    const STRADDLE_DEADZONE_PX = 6;

    let idleTimer: number | undefined;

    const settleStraddle = () => {
      // Step mode owns transitions when active — straddle settler
      // would fight its discrete stops. Re-evaluated per-call so the
      // settler engages automatically when the viewport collapses out
      // of step mode.
      if (isStepModeActive()) return;
      // Mobile / narrow / short: no pinning, so there's nothing to
      // straddle. Snapping here would hijack ordinary touch scrolling.
      if (!isDesktopStoryViewport()) return;
      if (isNavigating) return;
      const vh = window.innerHeight;

      for (const pin of targets) {
        const rect = pin.getBoundingClientRect();
        const topRatio = rect.top / vh;
        if (topRatio > STRADDLE_ENTER_MAX) continue;  // fully below fold
        if (topRatio < STRADDLE_EXIT_MIN) continue;   // already well past
        if (Math.abs(rect.top) < STRADDLE_DEADZONE_PX) continue; // docked

        // Snap forward into the pin if we're closer to its top than
        // to the pre-pin position (one viewport above). Otherwise
        // retreat to the previous boundary.
        const pinTopY = window.scrollY + rect.top;
        const targetY = rect.top > 0 && rect.top > vh * 0.5
          ? pinTopY - vh   // closer to exit point above — retreat
          : rect.top < 0 && rect.top < -vh * 0.3
            ? pinTopY      // we're mostly past; pull forward into pin top
            : pinTopY;     // default: dock into pin

        lenis.scrollTo(targetY, {
          duration: 0.6,
          easing: easeOutQuart,
        });
        return;
      }
    };

    const scheduleStraddle = () => {
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(settleStraddle, STRADDLE_IDLE_MS);
    };

    window.addEventListener('wheel', scheduleStraddle, { passive: true });
    window.addEventListener('touchmove', scheduleStraddle, { passive: true });
    window.addEventListener('touchend', scheduleStraddle, { passive: true });
    window.addEventListener('keydown', (e) => {
      const scrollKeys = [
        'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp',
        ' ', 'Home', 'End',
      ];
      if (scrollKeys.includes(e.key)) scheduleStraddle();
    });
  }
}

/* ------------------------------------------------------------------
 * PHILOSOPHY DOT GRIDS
 *
 * Panel 1's "industry vs. us" comparison renders one dot per day:
 * 180 grey dots in a 30-col × 6-row block, 14 red dots in a single row.
 * The shared column pitch makes the area ratio (180:14) visceral.
 * Each dot carries a --col custom property so CSS can stagger the
 * entrance left-to-right as a wave.
 * Must run BEFORE the reveal observer below — otherwise the dots
 * could be appended after .is-visible has already been added, and
 * their transition-delay would fire against an already-finished class.
 * ------------------------------------------------------------------ */
{
  const grids = document.querySelectorAll<HTMLElement>('.phil-compress__grid');
  for (const grid of grids) {
    const count = Number(grid.dataset.dots ?? 0);
    if (!count) continue;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('span');
      dot.className = 'phil-compress__dot';
      dot.style.setProperty('--col', String((i % 30) + 1));
      frag.appendChild(dot);
    }
    grid.appendChild(frag);
  }
}

/* ------------------------------------------------------------------
 * PARTNER SELECTOR DOTS
 *
 * The 10x10 partnership-fit selector is static HTML. Give each dot
 * deterministic delay values so CSS can make the points drop in when
 * the selector enters view.
 * ------------------------------------------------------------------ */
{
  const grids = document.querySelectorAll<HTMLElement>('.selector__grid');
  for (const grid of grids) {
    const dots = Array.from(grid.querySelectorAll<HTMLElement>('span'));
    /* Shuffle indices so each dot lands at an independent moment —
     * removing the row/column wave that made the grid read as one
     * coherent movement. The pick dot is forced to the end of the
     * order so it arrives as the climax. */
    const order = dots.map((_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    const pickIndex = dots.findIndex((d) => d.classList.contains('is-pick'));
    if (pickIndex >= 0) {
      const at = order.indexOf(pickIndex);
      if (at >= 0) {
        order.splice(at, 1);
        order.push(pickIndex);
      }
    }

    const baseDelay = 80;
    const stepDelay = 14;
    order.forEach((dotIndex, position) => {
      const dot = dots[dotIndex];
      /* Small per-dot jitter so neighbours in the shuffle order don't
       * fire on a perfect rhythm — keeps the arrivals feeling organic. */
      const jitter = Math.floor(Math.random() * 24) - 12;
      const dropDelay = baseDelay + position * stepDelay + jitter;
      dot.style.setProperty('--drop-delay', `${Math.max(0, dropDelay)}ms`);
      dot.style.setProperty('--pop-delay', `${Math.max(0, dropDelay + 160)}ms`);
    });
  }
}

/* ------------------------------------------------------------------
 * REVEAL ON SCROLL
 * ------------------------------------------------------------------ */
{
  const reveals = document.querySelectorAll<HTMLElement>('.reveal');
  if ('IntersectionObserver' in window && !reduceMotion) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -6% 0px' },
    );
    reveals.forEach((item) => revealObserver.observe(item));
  } else {
    reveals.forEach((item) => item.classList.add('is-visible'));
  }
}

/* ------------------------------------------------------------------
 * CONTACT CHAT — minimalist scripted conversation.
 * Asks four questions (name, company, email, reason), then asks for
 * a natural-language confirmation. No backend, no AI call — the "AI"
 * feel is a typewriter reveal on each prompt.
 * ------------------------------------------------------------------ */
{
  const cta = document.querySelector<HTMLElement>('[data-contact-cta]');
  const chatRoot = document.querySelector<HTMLElement>('[data-chat]');
  const chatLog = document.querySelector<HTMLOListElement>('[data-chat-log]');
  const chatForm = document.querySelector<HTMLFormElement>('[data-chat-form]');
  const chatInput = document.querySelector<HTMLInputElement>('[data-chat-input]');
  const chatSend = document.querySelector<HTMLButtonElement>('[data-chat-send]');
  const startBtn = document.querySelector<HTMLButtonElement>('[data-start-chat]');

  type StepKey = 'name' | 'company' | 'email' | 'reason';
  type Step = {
    key: StepKey;
    prompts: string[];
    placeholder: string;
    type?: 'text' | 'email';
    validate?: (value: string) => string | null;
  };

  const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* Prompt variants — a random entry is picked each time the chat asks,
   * so the flow reads like a live conversation instead of a canned
   * script. Every variant in a slot has to make sense regardless of
   * which variant was picked in the slot before it. */
  const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

  const steps: Step[] = [
    {
      key: 'name',
      prompts: [
        "Hi — what's your name?",
        "Hey there. What should we call you?",
        "Welcome. Who are we talking to?",
        "Glad you stopped by. What's your name?",
        "Quick intro — what's your name?",
        "First things first: what's your name?",
        "Let's start simple — your name?",
        "Hey. Mind telling us your name?",
        "Good to meet you. What's your name?",
        "Hello. What name do you go by?",
        "We'll keep this short. Start with your name?",
        "Before we dig in — your name?",
        "Hi there. What's the name?",
        "Welcome in. What do we call you?",
        "Hey. Let's start with your name.",
        "Hi. Can we get your name first?",
        "Alright, introductions first. Your name?",
        "Nice to see you. What's your name?",
        "Hi — who's on the other end?",
        "Welcome. Let's start with your name.",
        "Hey, happy you're here. What's your name?",
        "Hi there — a name to put to this?",
        "Good to see you. Your name?",
        "Quick one: what's your name?",
        "Starting with the basics — your name?",
      ],
      placeholder: 'e.g. Ada Lovelace',
      validate: (v) => (v.trim().length < 2 ? 'A name, please.' : null),
    },
    {
      key: 'company',
      prompts: [
        "Nice to meet you. Company or project?",
        "Got it. What's the company or project called?",
        "Noted. Where are you working on this — company or side project?",
        "Thanks. What's the name of the company or project?",
        "Good. Company, agency, or something you're building on your own?",
        "Alright. What's this for — company or personal project?",
        "Cool. What do you call the thing you're building?",
        "Perfect. Who do you work with, or what are you building?",
        "Great. Company or project name?",
        "Got you. What's the company or project?",
        "Noted. Who's this for?",
        "Appreciate it. What's the company or project name?",
        "Thanks. What are you working on, and where?",
        "Nice. What's the outfit — company or project?",
        "Good. Where does this live — company or a thing you're building?",
        "Got it. What's the name of the shop or project?",
        "Solid. Company, studio, or solo?",
        "Alright. Who's behind this?",
        "Cool. Where does this sit — company or project?",
        "Noted. Tell us the company or what you're calling the project?",
        "Good. What's the project or company name?",
        "Got it. A company or project name?",
        "Nice. Where is this based — company or solo build?",
        "Okay. What do we call the context — company or project?",
        "Thanks. Company name or project name works.",
      ],
      placeholder: 'Where you work, or what you call it',
      validate: (v) => (v.trim().length < 1 ? 'Give us something.' : null),
    },
    {
      key: 'email',
      prompts: [
        "Where can we reach you?",
        "What's the best email to reach you?",
        "Cool. Email address to follow up?",
        "Good. Best email for us to write back?",
        "Got it. Email to reach you at?",
        "Alright. Your email?",
        "Great. What's the email address?",
        "Noted. Where should we send a reply?",
        "Nice. Drop an email so we can write back?",
        "Perfect. Email for the follow-up?",
        "Got you. Best email to use?",
        "Thanks. What email should we use?",
        "Appreciate it. Email address?",
        "Good. Where do we write back?",
        "Solid. Your email, please?",
        "Cool. Reply address?",
        "Alright. Email to reach you on?",
        "Got it. What's the contact email?",
        "Thanks. Email, please.",
        "Nice. Where should replies go?",
        "Noted. Best way to reach you by email?",
        "Good. Give us an email to reply to?",
        "Okay. Email, so we can get back to you?",
        "Great. What email works best?",
        "Cool. Email to continue this on?",
      ],
      placeholder: 'you@company.com',
      type: 'email',
      validate: (v) => (emailLike.test(v.trim()) ? null : 'That doesn’t look like an email.'),
    },
    {
      key: 'reason',
      prompts: [
        "Last one — what do you want to build?",
        "Okay, the important one: what are you trying to build?",
        "Final question. What do you want to make?",
        "Last bit — what's the build?",
        "Alright, the big one. What are you building?",
        "One more. What do you want to build?",
        "Last question. What's the idea?",
        "Final one — what are we making together?",
        "Okay, last one. What do you want to build?",
        "Last thing. Tell us what you want to build.",
        "Wrapping up — what's the project about?",
        "Final stretch. What do you want to make?",
        "One more to go. What are you trying to build?",
        "Alright, last one. What's on your mind to build?",
        "Last round — what do you want to get made?",
        "Okay, the final one: what's the project?",
        "Last question. Describe what you want to build?",
        "Home stretch — what do you want to build?",
        "One last thing. What should we help you build?",
        "Final ask — what are you looking to build?",
        "Last one — what's the thing you want to make?",
        "Alright, wrap it up. What do you want to build?",
        "Final question. What would you like built?",
        "One more. What's the vision?",
        "Okay, final one. What are we building?",
      ],
      placeholder: 'A few words is enough.',
    },
  ];

  const reviewPrompts = [
    "Looks right? Type yes to send. Or edit your input above.",
    "Happy with that? Type yes to send, or edit above.",
    "All good? Hit yes to send, or fix anything above.",
    "Does that look right? Yes to send, or edit any answer.",
    "Ready to send? Type yes — or tweak anything above.",
    "Good to go? Type yes to send, or edit first.",
    "Looks good? Yes to send. Edit above if not.",
    "All set? Yes to send, or make changes above.",
    "Right so far? Yes to fire it off, or edit above.",
    "Does that read right? Yes to send, edit otherwise.",
    "Looking right? Say yes to send, or fix anything above.",
    "All correct? Yes to send, edit if not.",
    "Review above — yes to send, or change anything.",
    "Before we send — looks right? Yes, or edit above.",
    "Good on all that? Yes to send, or tweak first.",
    "Looks right to you? Yes to send, edits above.",
    "Everything check out? Yes sends it. Edit above if not.",
    "That accurate? Yes to send. Fix above if not.",
    "Match what you meant? Yes to send, or edit.",
    "Alright, sending? Yes to go, or edit above.",
    "Confirm? Yes sends it. Otherwise edit above.",
    "Ready? Type yes to send — edit above to change anything.",
    "Sound right? Yes to send, or fix above.",
    "If that's right, type yes. If not, edit above.",
    "Quick check — looks good? Yes to send, edit if not.",
  ];

  const finishTemplates = [
    "Thanks, {name}. We’ll be in touch at {email} within a day.",
    "Got it, {name}. Expect a reply at {email} within 24 hours.",
    "Appreciate it, {name}. We’ll write to {email} shortly.",
    "Thanks for sending this, {name}. You’ll hear from us at {email} soon.",
    "Noted, {name}. A reply’s heading to {email} within the day.",
    "Thanks, {name}. We’ll reach out to {email} within a day.",
    "On it, {name}. Look for a reply at {email} soon.",
    "Got you, {name}. Expect a note at {email} within 24 hours.",
    "Thanks, {name}. {email} will hear from us shortly.",
    "Sent, {name}. We’ll follow up at {email} within a day.",
    "Appreciated, {name}. A reply’s coming to {email} soon.",
    "Cheers, {name}. Expect something at {email} within a day.",
    "Got it, {name}. We’ll be in your inbox at {email} shortly.",
    "Thanks, {name}. Watch {email} for our reply — within a day.",
    "Noted, {name}. We’ll circle back via {email} within 24 hours.",
    "Nice one, {name}. You’ll get a note at {email} shortly.",
    "Perfect, {name}. We’ll hit you back at {email} within a day.",
    "Thanks, {name}. {email} gets a reply from us soon.",
    "Great, {name}. We’ll respond at {email} within the day.",
    "Solid, {name}. Expect a reply at {email} in under 24 hours.",
    "Done, {name}. A message is coming to {email} shortly.",
    "Thanks for that, {name}. {email} will hear back within a day.",
    "Got your note, {name}. We’ll write to {email} soon.",
    "Received, {name}. Expect a reply to {email} within 24 hours.",
    "Alright, {name}. We’ll get back to you at {email} within a day.",
  ];

  const answers: Partial<Record<StepKey, string>> = {};
  let stepIdx = 0;
  let busy = false;

  const TYPE_MIN_MS = 14;
  const TYPE_MAX_MS = 28;
  const TYPE_GAP_MS = 320;
  const BOTTOM_THRESHOLD_PX = 48;
  const OPEN_CHAT_PROMPT_DELAY_MS = 260;
  const OPEN_CHAT_CTA_HIDE_MS = 720;

  let shouldStickToBottom = true;
  let chatStarted = false;

  const isLogNearBottom = () => {
    if (!chatLog) return true;
    return (
      chatLog.scrollHeight - chatLog.scrollTop - chatLog.clientHeight
      <= BOTTOM_THRESHOLD_PX
    );
  };

  const scrollLogToEnd = (force = false) => {
    if (!chatLog) return;
    if (!force && !shouldStickToBottom && !isLogNearBottom()) return;
    chatLog.scrollTop = chatLog.scrollHeight;
    shouldStickToBottom = true;
  };

  chatLog?.addEventListener(
    'scroll',
    () => {
      shouldStickToBottom = isLogNearBottom();
    },
    { passive: true },
  );

  const userTurnsByKey: Partial<Record<StepKey, HTMLLIElement>> = {};

  const pencilSvg = `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
        fill="none" stroke="currentColor" stroke-width="1.6"
        stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  const checkSvg = `
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 8.3 6.4 12 13 4"
        fill="none" stroke="currentColor" stroke-width="1.5"
        stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  const closeSvg = `
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4.2 4.2 11.8 11.8M11.8 4.2 4.2 11.8"
        fill="none" stroke="currentColor" stroke-width="1.4"
        stroke-linecap="round"/>
    </svg>`;

  const setUserTurn = (text: string, key: StepKey) => {
    if (!chatLog) return;
    const existing = userTurnsByKey[key];
    if (existing) {
      const b = existing.querySelector<HTMLElement>('.chat__bubble');
      if (b) b.textContent = text;
      scrollLogToEnd(true);
      return;
    }
    const li = document.createElement('li');
    li.className = 'chat__turn chat__turn--user';

    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'chat__edit-btn';
    edit.setAttribute('aria-label', `Edit your ${key}`);
    edit.innerHTML = pencilSvg;
    edit.addEventListener('click', () => startEdit(key));

    const bubble = document.createElement('div');
    bubble.className = 'chat__bubble';
    bubble.textContent = text;

    li.append(edit, bubble);
    chatLog.appendChild(li);
    userTurnsByKey[key] = li;
    scrollLogToEnd(true);
  };

  const appendUserTurn = (text: string) => {
    if (!chatLog) return;
    const li = document.createElement('li');
    li.className = 'chat__turn chat__turn--user chat__turn--free';

    const bubble = document.createElement('div');
    bubble.className = 'chat__bubble';
    bubble.textContent = text;

    li.appendChild(bubble);
    chatLog.appendChild(li);
    scrollLogToEnd(true);
  };

  type InlineEdit = {
    key: StepKey;
    li: HTMLLIElement;
    editButton: HTMLButtonElement;
    bubble: HTMLElement;
    textarea: HTMLTextAreaElement;
    original: string;
  };

  let activeInlineEdit: InlineEdit | null = null;

  const closeInlineEdit = (nextText?: string) => {
    if (!activeInlineEdit) return;

    const { li, editButton, bubble, original } = activeInlineEdit;
    bubble.textContent = nextText ?? original;
    editButton.disabled = false;
    editButton.setAttribute('aria-expanded', 'false');
    li.classList.remove('is-editing');
    activeInlineEdit = null;
  };

  const startEdit = (key: StepKey) => {
    if (busy) return;

    const li = userTurnsByKey[key];
    const bubble = li?.querySelector<HTMLElement>('.chat__bubble');
    const editButton = li?.querySelector<HTMLButtonElement>('.chat__edit-btn');
    const step = steps.find((s) => s.key === key);
    if (!li || !bubble || !editButton || !step) return;

    if (activeInlineEdit?.key === key) {
      activeInlineEdit.textarea.focus({ preventScroll: true });
      return;
    }
    closeInlineEdit();

    const original = answers[key] ?? bubble.textContent ?? '';
    const form = document.createElement('form');
    form.className = 'chat__inline-edit';
    form.dataset.editKey = key;
    form.setAttribute('data-lenis-prevent', '');

    const textarea = document.createElement('textarea');
    textarea.className = 'chat__inline-input';
    textarea.value = original;
    textarea.rows = 1;
    textarea.spellcheck = false;
    textarea.setAttribute('aria-label', `Edit your ${key}`);

    const actions = document.createElement('div');
    actions.className = 'chat__inline-actions';

    const save = document.createElement('button');
    save.type = 'submit';
    save.className = 'chat__inline-save';
    save.setAttribute('aria-label', 'Save edit');
    save.innerHTML = checkSvg;

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'chat__inline-cancel';
    cancel.setAttribute('aria-label', 'Cancel edit');
    cancel.innerHTML = closeSvg;
    cancel.addEventListener('click', () => closeInlineEdit());

    actions.append(save, cancel);
    form.append(textarea, actions);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const value = textarea.value.trim();
      const error = step.validate ? step.validate(value) : null;
      if (error) {
        textarea.setCustomValidity(error);
        textarea.reportValidity();
        window.setTimeout(() => textarea.setCustomValidity(''), 0);
        return;
      }

      answers[key] = value;
      closeInlineEdit(value);
    });

    textarea.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeInlineEdit();
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        form.requestSubmit();
      }
    });
    textarea.addEventListener('input', () => syncInlineTextareaHeight(textarea));

    bubble.replaceChildren(form);
    editButton.disabled = true;
    editButton.setAttribute('aria-expanded', 'true');
    li.classList.add('is-editing');
    activeInlineEdit = { key, li, editButton, bubble, textarea, original };
    syncInlineTextareaHeight(textarea);
    textarea.focus({ preventScroll: true });
    textarea.select();
  };

  const syncInlineTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const appendAiTurn = (closing = false): HTMLElement => {
    if (!chatLog) throw new Error('no chat log');
    const wasNearBottom = isLogNearBottom();
    const li = document.createElement('li');
    li.className =
      'chat__turn chat__turn--ai' + (closing ? ' chat__turn--closing' : '');
    const prefix = document.createElement('span');
    prefix.className = 'chat__prefix';
    prefix.textContent = 'FI';
    const bubble = document.createElement('div');
    bubble.className = 'chat__bubble';
    const caret = document.createElement('span');
    caret.className = 'chat__caret-blink';
    caret.textContent = '█';
    bubble.appendChild(caret);
    li.appendChild(prefix);
    li.appendChild(bubble);
    chatLog.appendChild(li);
    shouldStickToBottom = wasNearBottom;
    scrollLogToEnd();
    return bubble;
  };

  const typeInto = (bubble: HTMLElement, text: string) =>
    new Promise<void>((resolve) => {
      const caret = bubble.querySelector<HTMLElement>('.chat__caret-blink');
      let i = 0;

      if (reduceMotion) {
        if (caret) caret.remove();
        bubble.textContent = text;
        resolve();
        return;
      }

      const step = () => {
        if (i >= text.length) {
          if (caret) caret.remove();
          resolve();
          return;
        }
        const ch = text[i++];
        const node = document.createTextNode(ch);
        if (caret) bubble.insertBefore(node, caret);
        else bubble.appendChild(node);
        // Keep the growing bubble pinned to the bottom of the scroll
        // window so long responses don't disappear above the fold.
        scrollLogToEnd();
        const delay = TYPE_MIN_MS + Math.random() * (TYPE_MAX_MS - TYPE_MIN_MS);
        window.setTimeout(step, delay);
      };
      window.setTimeout(step, TYPE_GAP_MS);
    });

  const setInputEnabled = (enabled: boolean, clear = true) => {
    if (!chatInput || !chatSend) return;
    /* readOnly (not disabled) so iOS keeps the keyboard up between
     * turns. `disabled` removes focus and dismisses the keyboard;
     * a follow-up programmatic focus() won't bring it back since iOS
     * only opens the keyboard in response to a real user gesture.
     * Always clear the initial-HTML `disabled` attribute too — it's
     * only there as a pre-JS no-input baseline. The send button still
     * uses `disabled` (it's a button, not a focusable input). */
    chatInput.disabled = false;
    chatInput.readOnly = !enabled;
    chatSend.disabled = !enabled;
    if (enabled) {
      if (clear) chatInput.value = '';
      chatInput.focus({ preventScroll: true });
    }
  };

  const ask = async (idx: number) => {
    const step = steps[idx];
    if (!step || !chatInput) return;
    busy = true;
    setInputEnabled(false);

    const bubble = appendAiTurn();
    chatInput.placeholder = step.placeholder;
    chatInput.type = step.type ?? 'text';

    await typeInto(bubble, pick(step.prompts));

    busy = false;
    setInputEnabled(true);
  };

  let awaitingReview = false;
  let reviewPromptShown = false;
  const isReviewConfirmation = (value: string) =>
    value.trim().toLowerCase() === 'yes';

  const showReview = async () => {
    if (!chatLog || !chatInput) return;
    awaitingReview = true;
    busy = true;
    setInputEnabled(false);
    chatForm?.removeAttribute('hidden');

    if (!reviewPromptShown) {
      const bubble = appendAiTurn();
      await typeInto(bubble, pick(reviewPrompts));
      reviewPromptShown = true;
    }

    chatInput.type = 'text';
    chatInput.placeholder = 'yes';
    busy = false;
    setInputEnabled(true);
  };

  /* Failure templates — shown when the /api/contact POST fails (no
   * network, server misconfigured, Resend down). Tells the submitter
   * how to reach us directly so a network blip doesn't lose the lead. */
  const failureTemplates = [
    "Hmm, our form didn't go through, {name}. Email us directly at hi@factory-innovations.com and we'll pick it up from there.",
    "Looks like that didn't send, {name}. Drop us a line at hi@factory-innovations.com — we'll get back to you fast.",
    "Couldn't deliver that, {name}. Try us at hi@factory-innovations.com and we'll respond within a day.",
  ];

  const finish = async () => {
    if (!chatInput) return;
    awaitingReview = false;
    busy = true;
    setInputEnabled(false);
    chatForm?.remove();

    const firstName = (answers.name || '').trim().split(/\s+/)[0] || 'there';
    const emailAddr = answers.email ?? '';

    /* Submit the collected fields to /api/contact. The endpoint
     * sends the inquiry email and adds the contact to the Resend
     * segment with the segment's expected custom properties
     * (company → company, reason → message). On failure we still
     * close the chat gracefully and direct the user to email
     * directly so a 500/network issue doesn't lose the lead. */
    let submitOk = false;
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: answers.name ?? '',
          company: answers.company ?? '',
          email: answers.email ?? '',
          reason: answers.reason ?? '',
        }),
      });
      submitOk = res.ok;
    } catch {
      submitOk = false;
    }

    const template = submitOk
      ? pick(finishTemplates)
      : pick(failureTemplates);
    const message = template
      .replace(/\{name\}/g, firstName)
      .replace(/\{email\}/g, emailAddr);
    const bubble = appendAiTurn(true);
    await typeInto(bubble, message);
  };

  const handleReviewReply = async (value: string) => {
    if (!chatInput) return;

    if (!isReviewConfirmation(value) || activeInlineEdit) {
      chatInput.value = '';
      chatInput.placeholder = activeInlineEdit
        ? 'save edit, then yes'
        : 'type yes to send';
      activeInlineEdit?.textarea.focus({ preventScroll: true });
      return;
    }

    appendUserTurn(value);
    chatInput.value = '';
    finish();
  };

  /* iOS Safari + on-screen keyboard:
   * When the input gets focus, iOS scrolls the layout viewport up to
   * bring the focused element into view. position:fixed elements (the
   * site header, the X close button) anchor to the layout viewport,
   * so they get scrolled out of sight along with everything else.
   * `overflow: hidden` on html/body doesn't stop this on iOS — Safari
   * still scrolls the document for the keyboard.
   *
   * The fix: track `window.visualViewport` (which DOES report the
   * actual visible region, accounting for keyboard + iOS scroll) and
   * push the offset onto a CSS custom property `--vv-top`. The chat
   * overlay, the X button, and the header all add `var(--vv-top)` to
   * their `top` so they stay glued to the visible viewport's edge.
   * `--vv-height` is also exposed so the chat can size itself to the
   * exact visible region. */
  let vvHandlersAttached = false;
  const updateVisualViewport = (): void => {
    const vv = window.visualViewport;
    if (!vv) return;
    document.documentElement.style.setProperty('--vv-top', `${vv.offsetTop}px`);
    document.documentElement.style.setProperty('--vv-height', `${vv.height}px`);
  };
  const attachVisualViewportTracking = (): void => {
    if (vvHandlersAttached) return;
    const vv = window.visualViewport;
    if (!vv) return;
    vv.addEventListener('resize', updateVisualViewport);
    vv.addEventListener('scroll', updateVisualViewport);
    vvHandlersAttached = true;
    updateVisualViewport();
  };
  const detachVisualViewportTracking = (): void => {
    if (!vvHandlersAttached) return;
    const vv = window.visualViewport;
    if (!vv) return;
    vv.removeEventListener('resize', updateVisualViewport);
    vv.removeEventListener('scroll', updateVisualViewport);
    vvHandlersAttached = false;
    document.documentElement.style.removeProperty('--vv-top');
    document.documentElement.style.removeProperty('--vv-height');
  };

  const openChat = () => {
    if (!chatRoot || !cta || chatStarted) return;
    chatStarted = true;
    const contactSection = document.querySelector<HTMLElement>('.contact');
    const heading = document.querySelector<HTMLElement>('[data-contact-heading]');

    // FLIP: snapshot the heading's current Y, toggle the layout,
    // then animate it back to zero so it smoothly slides up from
    // its centered position to the new top-anchored position.
    const firstTop = heading?.getBoundingClientRect().top ?? 0;

    contactSection?.classList.add('is-chatting');
    cta.classList.add('is-dismissed');
    chatRoot.removeAttribute('hidden');

    if (heading && !reduceMotion) {
      const lastTop = heading.getBoundingClientRect().top;
      const delta = firstTop - lastTop;
      if (delta !== 0) {
        heading.style.transition = 'none';
        heading.style.transform = `translateY(${delta}px)`;
        // force reflow so the starting transform is committed
        void heading.offsetHeight;
        heading.style.transition = 'transform 620ms cubic-bezier(.22, 1, .36, 1)';
        heading.style.transform = 'translateY(0)';
        const cleanup = () => {
          heading.style.transition = '';
          heading.style.transform = '';
          heading.removeEventListener('transitionend', cleanup);
        };
        heading.addEventListener('transitionend', cleanup);
      }
    }

    // Force a layout boundary so the composer can visibly travel from
    // its raised pre-chat position down into the active input rail.
    void chatRoot.offsetHeight;
    chatRoot.classList.add('is-visible');
    scrollLogToEnd(true);

    // Mobile overlay flag: disables page scroll-snap and smooth-scroll
    // so iOS keyboard resizes can't yank the page behind the overlay.
    document.documentElement.classList.add('is-chat-open');
    attachVisualViewportTracking();

    window.setTimeout(() => {
      cta.setAttribute('hidden', '');
    }, reduceMotion ? 0 : OPEN_CHAT_CTA_HIDE_MS);

    window.setTimeout(() => {
      scrollLogToEnd(true);
      ask(0);
    }, reduceMotion ? 0 : OPEN_CHAT_PROMPT_DELAY_MS);
  };

  /* Close button — visually only on mobile (the overlay layout) but
   * wired up unconditionally so the same affordance works at narrow
   * desktop. Closing fully resets chat state so re-tapping "Start the
   * conversation" begins a fresh thread; the contact section reverts
   * to its pre-chat layout. (Preserving state across close/reopen
   * would require split init/show paths and isn't worth the
   * complexity for a CTA-style chat.) */
  const closeChat = (): void => {
    if (!chatRoot) return;
    chatRoot.classList.remove('is-visible');
    chatRoot.setAttribute('hidden', '');
    const contactSection = document.querySelector<HTMLElement>('.contact');
    contactSection?.classList.remove('is-chatting');
    cta?.classList.remove('is-dismissed');
    cta?.removeAttribute('hidden');
    document.documentElement.classList.remove('is-chat-open');
    detachVisualViewportTracking();
    chatInput?.blur();
    // Reset state so a re-open starts a fresh conversation.
    chatStarted = false;
    busy = false;
    stepIdx = 0;
    awaitingReview = false;
    for (const k of Object.keys(answers) as StepKey[]) delete answers[k];
    for (const k of Object.keys(userTurnsByKey) as StepKey[]) {
      delete userTurnsByKey[k];
    }
    if (chatLog) chatLog.innerHTML = '';
    setInputEnabled(false, true);
  };

  document
    .querySelector<HTMLButtonElement>('[data-chat-close]')
    ?.addEventListener('click', closeChat);

  startBtn?.addEventListener('click', openChat);

  chatForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    if (busy || !chatInput) return;

    const value = chatInput.value.trim();
    if (!value) return;

    if (awaitingReview) {
      handleReviewReply(value);
      return;
    }

    const step = steps[stepIdx];
    if (!step) return;

    const error = step.validate ? step.validate(value) : null;
    if (error) {
      chatInput.setCustomValidity(error);
      chatInput.reportValidity();
      window.setTimeout(() => chatInput.setCustomValidity(''), 0);
      return;
    }

    answers[step.key] = value;
    setUserTurn(value, step.key);

    stepIdx += 1;
    if (stepIdx >= steps.length) {
      showReview();
    } else {
      ask(stepIdx);
    }
  });
}

/* ------------------------------------------------------------------
 * JOBS — apply form submission. Each job card on /jobs has a
 * [data-apply-form] that POSTs to /api/apply (mirrors /api/contact).
 * One handler binds all forms so adding a new job card is HTML-only.
 * No-op on pages that don't have the form.
 * ------------------------------------------------------------------ */
{
  const forms = document.querySelectorAll<HTMLFormElement>('[data-apply-form]');
  if (forms.length) {
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const MAX_CV_BYTES = 5 * 1024 * 1024; // 5 MB

    const setStatus = (
      el: HTMLElement | null,
      text: string,
      state: '' | 'ok' | 'error',
    ): void => {
      if (!el) return;
      el.textContent = text;
      if (state) el.setAttribute('data-state', state);
      else el.removeAttribute('data-state');
    };

    /* Read a File as base64 (without the data: prefix). FileReader gives
     * us a "data:application/pdf;base64,XXXX…" string; split on the comma
     * to get the raw payload the Resend attachments API expects. */
    const fileToBase64 = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          if (typeof result !== 'string') {
            reject(new Error('unexpected_reader_result'));
            return;
          }
          const idx = result.indexOf(',');
          resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        reader.onerror = () => reject(reader.error ?? new Error('read_failed'));
        reader.readAsDataURL(file);
      });

    forms.forEach((form) => {
      const submit = form.querySelector<HTMLButtonElement>('[data-apply-submit]');
      const status = form.querySelector<HTMLElement>('[data-apply-status]');
      const fileInput = form.querySelector<HTMLInputElement>('[data-apply-file]');
      const fileText = form.querySelector<HTMLElement>('[data-apply-file-text]');
      const fileWrap = form.querySelector<HTMLElement>('[data-apply-file-display]');

      /* Reflect the picked filename in the styled display. Native file
       * inputs are hidden via CSS so the wrapper is what the user sees. */
      fileInput?.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (file) {
          if (fileText) fileText.textContent = file.name;
          fileWrap?.setAttribute('data-has-file', '');
        } else {
          if (fileText) fileText.textContent = 'PDF auswählen';
          fileWrap?.removeAttribute('data-has-file');
        }
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;

        const data = new FormData(form);
        const payload = {
          first_name: String(data.get('first_name') ?? '').trim(),
          last_name: String(data.get('last_name') ?? '').trim(),
          email: String(data.get('email') ?? '').trim(),
          phone: String(data.get('phone') ?? '').trim(),
          role: String(data.get('role') ?? '').trim(),
          link: String(data.get('link') ?? '').trim(),
          message: String(data.get('message') ?? '').trim(),
          cv_filename: '',
          cv_base64: '',
        };

        if (payload.first_name.length < 1 || payload.last_name.length < 1) {
          setStatus(status, 'Bitte gib deinen Vor- und Nachnamen an.', 'error');
          return;
        }
        if (!EMAIL_RE.test(payload.email)) {
          setStatus(status, 'Bitte gib eine gültige E-Mail-Adresse an.', 'error');
          return;
        }
        if (payload.phone.length < 4) {
          setStatus(status, 'Bitte gib eine Telefonnummer an, unter der wir dich erreichen können.', 'error');
          return;
        }

        const file = fileInput?.files?.[0];
        if (!file) {
          setStatus(status, 'Bitte hänge deinen Lebenslauf oder Referenzen als PDF an.', 'error');
          return;
        }
        const isPdf =
          file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
        if (!isPdf) {
          setStatus(status, 'Es werden ausschließlich PDF-Dateien akzeptiert.', 'error');
          return;
        }
        if (file.size > MAX_CV_BYTES) {
          setStatus(status, 'PDF ist größer als 5 MB — bitte wähle eine kleinere Datei.', 'error');
          return;
        }

        if (submit) submit.disabled = true;
        setStatus(status, 'Wird gesendet…', '');

        try {
          payload.cv_filename = file.name;
          payload.cv_base64 = await fileToBase64(file);

          const res = await fetch('/api/apply', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!res.ok) throw new Error(String(res.status));

          /* Success — swap the form for a confirmation note. The
           * card stays open so the user sees the result in place. */
          const wrap = form.closest('.job-apply');
          if (wrap) {
            wrap.classList.add('is-sent');
            let done = wrap.querySelector<HTMLElement>('.job-apply__done');
            if (!done) {
              done = document.createElement('div');
              done.className = 'job-apply__done';
              wrap.appendChild(done);
            }
            const firstName = payload.first_name || 'Hallo';
            done.textContent =
              `Danke, ${firstName}. Deine Bewerbung für ${payload.role} ist eingegangen. ` +
              `Wir melden uns unter ${payload.email}.`;
          } else {
            setStatus(status, 'Bewerbung gesendet. Wir melden uns.', 'ok');
          }
        } catch {
          setStatus(
            status,
            'Konnte nicht gesendet werden. Bitte schreib uns direkt an hi@factory-innovations.com.',
            'error',
          );
          if (submit) submit.disabled = false;
        }
      });
    });
  }
}

requestAnimationFrame(() => {
  document.documentElement.classList.remove('is-booting');
});
